// The ONE client route-tessellation policy (taxgw-2ybg): shared by the war network's edge lines and the VFX
// route-ribbon cache, so both walk an authoritative SurfacePath with the same step and the same setup-wide budget.
// Sampling is delegated to content's forEachPathSample, which walks leg by leg and emits every control point, so a
// consumer never branches on the path's kind.

import { forEachPathSample, type SurfacePath } from '@taxwar/content/geometry/routes'
import { InvariantViolation } from '@taxwar/net'

/** Angular step between tessellated route points (radians); current visual density. */
export const ROUTE_STEP_RAD = 0.01

/**
 * Most route centre points one setup may tessellate to, summed over every edge (1 << 20). The route-ribbon cache
 * turns each centre point into two strip vertices of position (12 B), side (12 B), distance/across (8 B) and
 * progress (4 B), so 1M points ⇒ 2M vertices ⇒ <= 72 MiB of attributes plus <= 24 MiB of Uint32 indices: a fixed
 * source bound just under 100 MiB worst case. This is a budget, not a measured runtime capacity.
 */
export const MAX_ROUTE_CENTRE_POINTS = 1 << 20

/**
 * Counts the centre points `forEachPathSample` emits for `path`, throwing as soon as the running count exceeds
 * `budget`, so an oversized setup aborts before any buffer is allocated. Pass the setup's remaining budget when
 * summing across edges.
 */
export function countRouteSamples(path: SurfacePath, budget: number): number {
  let count = 0
  forEachPathSample(path, ROUTE_STEP_RAD, () => {
    count++
    if (count > budget) {
      throw new InvariantViolation(`route tessellation needs more than the setup's ${MAX_ROUTE_CENTRE_POINTS}-point budget; reduce the setup route detail instead of allocating an oversized buffer`)
    }
  })
  return count
}
